
import { LinkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useEffect, useState } from 'react';

import { Image } from '@/components/core/Image';
import { useDataModel } from '@/hooks/useDataModel';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import useEnumAttributeService from '@/hooks/useEnumAttributeService';
import { getFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';
import { DataModelAttribute, DataModelAttributeBaseClass, DataModelAttributeTypeName, Item } from '@/types/DataModel';

import { IconButton } from '../core/IconButton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../core/Table';
import { getMemberAttributes } from '../datamodel/Collection/useMemberAttributes';
import { useAttributeLabelTranslator } from '../datamodel/hooks/useAttributeLabelTranslator';
import { useCachedAdditionalCategory } from '../datamodel/hooks/useCachedAdditionalCategory';
import { useOptionTranslator } from '../datamodel/hooks/useOptionTranslator';
import FilePreview from './FilePreview';
import GroupRender from './GroupRender';
import { isWebLinkRenderer } from './webLinkRenderer';

interface CatalogAttributeProps {
    attribute: DataModelAttribute;
    item: Item;
    withoutLabel?: boolean;
    showImage?: boolean;
    gridMode?: boolean;
    /**
     * When true, render the attribute value on a single line and clip the
     * overflow with an ellipsis (mirrors the legacy Angular
     * `text-ellipsis overflow-hidden white-space-no-wrap` treatment used
     * in the Dimensional catalog template). Callers that opt in should
     * also set a `title` on their outer wrapper so the browser shows the
     * full value on hover. Used by the browse and catalog hit lists.
     */
    singleLine?: boolean;
}

const CatalogAttribute = ({
    attribute,
    item,
    withoutLabel = false,
    showImage = true,
    gridMode = false,
    singleLine = false
}: CatalogAttributeProps) => {
    const attributeData = item[attribute.name];
    const additionalDataModelName = attribute.params?.additionalModule;
    const extension = attribute.params?.extension;
    const { translateOption } = useOptionTranslator();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { formatDate } = useDateFormatter();
    const { dataModel } = useDataModel();
    const { enumAttributeService } = useEnumAttributeService();
    const [options, setOptions] = useState<any[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    const [extendedAttributeData, setExtendedAttributeData] = useState<any[]>([]);

    const { cachedAdditionalCategory, isLoading: isLoadingSelectedOption } =
        useCachedAdditionalCategory(attributeData, additionalDataModelName, extension);

    useEffect(() => {
        if (
            attribute.typeName === DataModelAttributeTypeName.Enum ||
            attribute.typeName === DataModelAttributeTypeName.Codelist
        ) {
            enumAttributeService?.getAttributeOptionsAsync(attribute, true).then(
                (fetchedOptions: any[]) => {
                    setOptions(fetchedOptions);
                    setLoadingOptions(false);
                }
            );
        }
    }, [attribute, enumAttributeService]);

    // Fetch full data for referenced items if MultiReference
    useEffect(() => {
        const fetchReferencedItems = async () => {
            if (
                attribute.typeName === DataModelAttributeTypeName.MultiReference &&
                Array.isArray(attributeData) &&
                attributeData.length > 0 &&
                dataModel
            ) {
                try {
                    const memberAttributes = getMemberAttributes(attribute, dataModel);
                    const response = await axios.post(restUrl('findByPrimaryKey'), {
                        items: attributeData.map((item: any) => item.primaryKey__),
                        fields: memberAttributes?.map(attr => attr.name),
                    });

                    const loadedItems = response.data;

                    const merged = attributeData.map((refItem: any) => {
                        const fullItem = loadedItems.find((item: any) => item.primaryKey__ === refItem.primaryKey__);
                        return { ...refItem, ...fullItem };
                    });

                    setExtendedAttributeData(merged);
                } catch (error) {
                    logger.error('Failed to load extended reference data:', error);
                    setExtendedAttributeData(attributeData); // fallback
                }
            }
        };

        fetchReferencedItems();
    }, [attribute, attributeData, dataModel]);

    const getOptionWithIconUrl = (options: any[], key: string) => {
        const selectedOption = options?.find(option => option.key === key);
        return selectedOption ? selectedOption.translatedOptionIconUrl : undefined;
    };

    const getLinkTemplate = (attr: any, link: string) => {
        if (isWebLinkRenderer(attr)) {
            return (
                <button
                    onClick={() => {
                        if (!link) return;
                        window.open(link, '_blank');
                    }}
                    className="tw-p-0 tw-bg-transparent tw-border-none hover:tw-text-blue-700"
                >
                    <div className="tw-flex tw-justify-center">
                        <IconButton
                            icon={LinkIcon}
                            variant="default"
                            size="sm"
                            className={gridMode ? 'hit-list-link-icon tw-h-4 tw-icon-inactive' : 'tw-h-4'}
                        />
                    </div>
                </button>
            );
        }
        return <div>{attributeData != null ? String(attributeData) : ''}</div>;
    };

    const getAttributeData = () => {
        if (isWebLinkRenderer(attribute)) {
            return getLinkTemplate(attribute, attributeData);
        }

        switch (attribute.typeName || attribute.baseClass) {
            case DataModelAttributeTypeName.Document: {
                // Legacy grid rendered Document cells through <file-thumbnail>
                // (application-controller.js generateCellEditorForDocumentOrImageType):
                // MIME-sniff the URL, then show a small drawable thumbnail
                // or a mime-typed file icon. `FilePreview showAsIconPreview`
                // is the direct React port of that behaviour (also handles
                // the "preview failed" state), so both the browse hit list
                // and the catalog view now show an icon instead of the
                // raw URL. Kept inside an <a> so the icon is still a link
                // (matches the legacy anchor-around-icon markup).
                //
                // NonDrawableURLPreview hardcodes `tw-h-8 tw-w-8` on its
                // file-type SVGs and the fallback `<Icon size="2xl">`
                // baked-in class, so the hit list overrides them.
                // Icons stay 24px (`tw-w-6`). Image thumbnails in the
                // table are 20px (`tw-w-5`).
                //
                // In gridMode (browse + catalog hit lists) we stretch the
                // <a> to the full cell so `justify-center` puts the icon
                // in the middle of the column. Non-grid callers (item
                // details, storybook) keep the icon inline so the value
                // still sits next to its label.
                const iconSizeOverride = gridMode
                    ? '[&_svg]:!tw-h-6 [&_svg]:!tw-w-6 [&_img]:!tw-h-5 [&_img]:!tw-w-5'
                    : '[&_svg]:!tw-h-6 [&_svg]:!tw-w-6 [&_img]:!tw-h-6 [&_img]:!tw-w-6';
                const linkClass = gridMode
                    ? `tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full ${iconSizeOverride}`
                    : `tw-inline-flex tw-items-center ${iconSizeOverride}`;
                return (
                    <a
                        href={attributeData}
                        target="_blank"
                        rel="noreferrer"
                        title={attributeData}
                        className={linkClass}
                    >
                        <FilePreview
                            url={attributeData}
                            showAsIconPreview={true}
                            mutedIcon={gridMode}
                            className="tw-object-contain"
                        />
                    </a>
                );
            }

            case DataModelAttributeTypeName.Image:
                return (
                    <Image
                        className={gridMode
                            ? 'tw-h-5 tw-w-5 tw-shrink-0 tw-object-contain tw-transition-none'
                            : 'tw-w-full tw-h-6 tw-object-contain tw-transition-none'}
                        src={gridMode
                            ? `${attributeData}?w=40&h=40&fit=crop`
                            : `${attributeData}?w=250&h=26&fit=crop`}
                    />
                );

            case DataModelAttributeTypeName.Enum:
            case DataModelAttributeTypeName.Codelist:
            case DataModelAttributeTypeName.OpenEnum:
            case DataModelAttributeTypeName.OpenCodelist:
            case DataModelAttributeTypeName.Boolean:
            { if (loadingOptions) {
                return <span>{translateOption(attributeData, attribute)}</span>;
            }

            const translatedOptionIconUrl = getOptionWithIconUrl(options, attributeData);
            const translation = translateOption(attributeData, attribute, options);

            if (translatedOptionIconUrl) {
                return (
                    <span className={singleLine
                        ? 'tw-inline-flex tw-items-center tw-min-w-0 tw-max-w-full'
                        : 'tw-inline-flex tw-items-center'}>
                        <Image
                            src={translatedOptionIconUrl}
                            height={singleLine ? 20 : 12}
                            width={singleLine ? 20 : undefined}
                            alt={translation}
                            data-option-flag={singleLine ? '' : undefined}
                            className={singleLine ? 'tw-h-5 tw-w-5 tw-shrink-0 tw-object-contain' : undefined}
                        />
                        <span className={singleLine ? 'tw-ml-2 tw-min-w-0 tw-truncate' : 'tw-ml-2'}>
                            {translation}
                        </span>
                    </span>
                );
            }
            return <span>{translation}</span>;
            }
            case DataModelAttributeTypeName.EnumSet:
            case DataModelAttributeTypeName.CodelistSet:
            case DataModelAttributeTypeName.OpenEnumSet:
            case DataModelAttributeTypeName.OpenCodelistSet:
                return Object.entries(attributeData).map((data, index: number) => <div key={index}>{translateOption(data.toString(), attribute, attribute.options)}</div>);

            case DataModelAttributeTypeName.Date:
                return <span>{formatDate(attributeData as string, 'mediumDate')}</span>;

            case DataModelAttributeTypeName.DateTime:
                return <span>{formatDate(attributeData as string, 'medium')}</span>;

            case DataModelAttributeTypeName.Physical: {
                const filter = getFilter('formatPhysicalValue');
                return <span>{filter ? filter(attributeData) : attributeData}</span>;
            }

            case DataModelAttributeTypeName.AdditionalCategory:
                if (isLoadingSelectedOption || !cachedAdditionalCategory) {
                    return <span>{attributeData}</span>;
                }
                return <span>{`${cachedAdditionalCategory.title} [${cachedAdditionalCategory.code}]`}</span>;

            case DataModelAttributeTypeName.Collection:
            case DataModelAttributeTypeName.MultiDimensional:
            case DataModelAttributeTypeName.MultiReference: {
                const memberAttributes = getMemberAttributes(attribute, dataModel);
                const displayData = attribute.typeName === DataModelAttributeTypeName.MultiReference
                    ? extendedAttributeData
                    : attributeData;

                return (
                    <div className='tw-overflow-auto tw-mt-3 border-table'>
                        <Table className="tw-table-fixed tw-border-collapse unset-width" containerClassName="tw-h-full">
                            <TableHeader>
                                <TableRow>
                                    {memberAttributes?.map((data: DataModelAttribute, index: number) => (
                                        <TableHead key={index}>{data.label}</TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {displayData?.map((attrData: any, i: number) => (
                                    <TableRow key={i}>
                                        {memberAttributes?.map((data: DataModelAttribute, index: number) => (
                                            <TableCell key={index}>
                                                {attrData[data.name] ? (
                                                    <CatalogAttribute
                                                        attribute={data}
                                                        item={attrData}
                                                        withoutLabel={true}
                                                        showImage={true}
                                                        gridMode = {true}
                                                    />
                                                ) : null}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                );
            }

            case DataModelAttributeTypeName.SingleReference:
                return <div>{attributeData.toString().replace('::', '-')}</div>;

            case DataModelAttributeTypeName.Dimensional:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (gridMode) {
                    return Object.entries(attributeData)
                        .slice(0, 1) // Take only the first entry
                        .map(([key, value]: any) => (
                            <div key={key}>
                                {value}
                            </div>
                        ));
                }
                return Object.entries(attributeData).map(([key, value]: any) => <div key={key}><strong>{translateOption(key, attribute, attribute.options)}</strong> : {value}</div>);

            case DataModelAttributeTypeName.Group:
                return <GroupRender attributeData={attributeData} attribute={attribute} />;

            case DataModelAttributeBaseClass.Hash:
                return <div>{JSON.stringify(attributeData)}</div>;

            default:
                return getLinkTemplate(attribute, attributeData);
        }
    };

    // Single-line variant collapses the value to one row and clips
    // overflow with an ellipsis so it fits inside a fixed-height grid
    // cell. Padding is dropped in this mode because the outer grid cell
    // already contributes its own padding and the extra `pb-2 / pl-3 /
    // mt-2` push the text down and steal horizontal room from the
    // ellipsis. The label branch is not used together with `singleLine`
    // in practice (grid callers pass `withoutLabel`), so it keeps its
    // usual wrapping styles when it does render.
    //
    // CSS `text-overflow: ellipsis` only draws the ellipsis on the block
    // whose OWN inline text overflows - it does not flow through nested
    // block children. Several `getAttributeData()` branches wrap the
    // value in a `<div>` (`<div>{string}</div>` for the default String
    // template, `<div>{ref}</div>` for SingleReference, `<div>{value}</div>`
    // for Dimensional in gridMode, `<div>{JSON}</div>` for Hash, etc.),
    // and without `[&_div]:tw-truncate` those inner divs would be
    // block-level children whose overflow was hidden by the parent but
    // never truncated with an ellipsis. Inline branches (`<span>` /
    // `<a>` value text) already flow through the parent block's
    // ellipsis and need no per-element treatment.
    const linkInGrid = gridMode && isWebLinkRenderer(attribute);
    const rootClassName = singleLine
        ? `catalog-attribute tw-min-w-0 tw-text-t-sm lg:tw-text-t-base tw-text-t-primary ${
            linkInGrid
                ? 'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center'
                : 'tw-max-w-full tw-truncate'
        }`
        : 'catalog-attribute tw-pb-2 tw-whitespace-normal tw-break-words';
    const valueClassName = singleLine
        ? linkInGrid
            ? 'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center'
            : 'tw-min-w-0 tw-max-w-full tw-truncate [&_div]:tw-truncate'
        : 'tw-pl-3 tw-mt-2';

    if ((!(attribute.typeName === DataModelAttributeTypeName.Image) || showImage))
        return (
            <div className={rootClassName}>
                {!withoutLabel && (
                    <label className="tw-font-bold tw-text-t-sm">
                        {translateAttributeLabel(attribute)}
                    </label>
                )}
                <div className={valueClassName}>{getAttributeData()}</div>
            </div>
        );
};

export default CatalogAttribute;
